import React, { useState } from 'react'
import Select, { MultiValue, SingleValue } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { Skeleton } from './Skeleton'
import { ErrorText, Label } from './Input'
import styled from '@emotion/styled'
import { ChangeHandler } from 'react-hook-form'

const SelectBase = styled.div`
  .react-select__placeholder {
    color: ${({ theme }) => theme};
  }

  .react-select__control {
    border-radius: 0;
    padding: 0.3rem 0.25rem;
    margin: 0;
  }

  .react-select__menu {
    border-radius: 0;
    margin-top: 0.05rem;
  }
`

const Subtext = styled.div`
  color: ${({ theme }) => theme.onBackgroundLight};
  font-size: 0.75rem;
  margin-top: 0.25rem;
`

interface DropdownOption {
  value: string | number
  label: string
}

interface DropdownProps {
  label?: string
  options: DropdownOption[]
  skeleton?: boolean
  required?: boolean
  placeholder?: string
  value?: string | number
  onChange?: ChangeHandler
  onBlur?: ChangeHandler
  name?: string
  disabled?: boolean
  clearable?: boolean
  error?: string
  subtext?: string
}

export const Dropdown: React.FC<DropdownProps & { isMulti?: boolean }> = ({
  label,
  skeleton,
  options,
  required,
  placeholder,
  value,
  onChange,
  onBlur,
  name,
  disabled,
  clearable,
  error,
  subtext,
  isMulti,
}) => {
  return (
    <SelectBase>
      {!!label && (
        <div>
          <Label>
            <Skeleton display="inline-block" active={skeleton}>
              {label}
              {!!required && (
                <span style={{ color: 'crimson', fontSize: '0.9rem', opacity: skeleton ? 0 : 1 }}> *</span>
              )}
            </Skeleton>
          </Label>
        </div>
      )}
      <Select
        isMulti={isMulti}
        classNamePrefix="react-select"
        options={options}
        placeholder={placeholder ?? ''}
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        noOptionsMessage={() => 'Inga alternativ'}
        value={options.find((option) => option.value === value)}
        onChange={(val) => {
          if (isMultiValue(val)) {
            onChange?.({ target: { value: val.map((v) => v.value), name } })
            return
          }

          onChange?.({ target: { value: val?.value, name } })
        }}
        onBlur={onBlur}
        name={name}
        isDisabled={disabled}
        isClearable={clearable}
      />
      {error && <ErrorText error={error} />}
      {subtext && <Subtext>{subtext}</Subtext>}
    </SelectBase>
  )
}

function isMultiValue<T>(arg: MultiValue<T> | SingleValue<T>): arg is MultiValue<T> {
  return Array.isArray(arg)
}

interface CreatableDropdownProps extends DropdownProps {
  onCreate?: (value: string) => void
  enablePreliminaryCreation?: boolean
  preliminaryCreationLabel?: (value: string) => string
  createWording?: (item: string) => string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CreatableDropdown = React.forwardRef<any, CreatableDropdownProps>(
  (
    {
      label,
      skeleton,
      options,
      required,
      placeholder,
      value,
      onChange,
      onBlur,
      name,
      disabled,
      onCreate,
      clearable,
      enablePreliminaryCreation,
      preliminaryCreationLabel,
      error,
      subtext,
      createWording,
    },
    ref,
  ) => {
    const [toCreate, setToCreate] = useState<null | DropdownOption>(null)

    return (
      <SelectBase>
        {!!label && (
          <div>
            <Label>
              <Skeleton display="inline-block" active={skeleton}>
                {label}
                {!!required && (
                  <span style={{ color: 'crimson', fontSize: '0.9rem', opacity: skeleton ? 0 : 1 }}> *</span>
                )}
              </Skeleton>
            </Label>
          </div>
        )}
        <CreatableSelect
          ref={ref}
          classNamePrefix="react-select"
          options={toCreate ? [...options, toCreate] : options}
          placeholder={placeholder ?? ''}
          noOptionsMessage={() => 'Inga alternativ'}
          onCreateOption={(result) => {
            if (enablePreliminaryCreation) {
              setToCreate({ value: result, label: result })
              onChange?.({ target: { value: result, name } })
              return
            }

            onCreate?.(result)
          }}
          value={toCreate ?? options.find((option) => option.value === value)}
          onChange={(val) => {
            if (enablePreliminaryCreation) {
              if (toCreate && toCreate.value !== val?.value) {
                setToCreate(null)
              }
            }

            onChange?.({ target: { value: val?.value, name } })
          }}
          onBlur={onBlur}
          name={name}
          isDisabled={disabled}
          formatCreateLabel={(userInput) => createWording?.(userInput) ?? `Skapa "${userInput}"`}
          isClearable={clearable}
        />
        {error && <ErrorText error={error} />}
        {(subtext || (preliminaryCreationLabel && toCreate?.value)) && (
          <Subtext>{subtext || preliminaryCreationLabel?.(toCreate?.value?.toString() ?? '')}</Subtext>
        )}
      </SelectBase>
    )
  },
)

CreatableDropdown.displayName = 'CreatableDropdown'
